// ─── חשיפות התיק · what sits behind the holdings ─────────────────────────────
//
// Five measurements of one portfolio, each a share of the SAME total: the
// value of every holding that has a price, exactly as the portfolio screen
// sums it (PortfolioTab: quote !== "none", price × shares). A holding without
// a quote is outside every total here, as it is there, and is named in the
// gaps list instead of being silently dropped.
//
// Every section adds to 100% of that total, with the part we cannot place
// stated as its own line. Nothing here grades the portfolio: no score, no
// norm, no "too much". A share is a measured fact and the reader decides what
// it means (VISION.md, "כללי תוכן קשיחים").

import { computeExposure } from "./exposure";
import { SECTOR_ETF, SECTOR_UNKNOWN } from "./sectorClassify";
import { noQuoteText } from "./quoteState";

// ── The shared base ──────────────────────────────────────────────────────────
export function valuedHoldings(portfolio = []) {
  const priced = portfolio.filter(p => p.quote !== "none" && Number(p.currentPrice) > 0 && Number(p.shares) > 0);
  const total = priced.reduce((a, p) => a + p.currentPrice * p.shares, 0);
  const rows = priced
    .map(p => ({ ...p, value: p.currentPrice * p.shares, weightPct: total > 0 ? (p.currentPrice * p.shares / total) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);
  const unpriced = portfolio.filter(p => !priced.includes(p));
  return { rows, total, unpriced };
}

// ── 1 · Concentration ────────────────────────────────────────────────────────
export function concentration(base, top = 5) {
  const rows = base.rows.map(r => ({ symbol: r.symbol, name: r.name, value: r.value, weightPct: r.weightPct }));
  const n = Math.min(top, rows.length);
  const topPct = rows.slice(0, n).reduce((a, r) => a + r.weightPct, 0);
  return { rows, topN: n, topPct, count: rows.length };
}

// ── 2 · Sectors ──────────────────────────────────────────────────────────────
// The taxonomy every other screen speaks (sectorClassify.js). A holding whose
// sector has not resolved yet is its own line, "טרם סווג" · not folded into
// "אחר", which is a classification Finnhub returned.
export const SECTOR_PENDING = "טרם סווג";

export function sectors(base) {
  const by = new Map();
  for (const r of base.rows) {
    const s = r.sector || SECTOR_PENDING;
    const cur = by.get(s) || { sector: s, weightPct: 0, symbols: [] };
    cur.weightPct += r.weightPct;
    cur.symbols.push(r.symbol);
    by.set(s, cur);
  }
  const rows = [...by.values()].sort((a, b) => {
    // The two lines that are not sectors go last, whatever their size.
    const tail = x => (x.sector === SECTOR_PENDING ? 2 : x.sector === SECTOR_UNKNOWN ? 1 : 0);
    return (tail(a) - tail(b)) || (b.weightPct - a.weightPct);
  });
  return rows;
}

// ── 3 · Currency ─────────────────────────────────────────────────────────────
// Holdings carry no currency field. The app prices a symbol only through
// Finnhub's US-exchange quotes (CLAUDE.md), so every holding with a price is
// quoted in dollars · and that is the claim made, with its basis.
export function currencies(base) {
  if (!base.rows.length) return [];
  return [{ code: "USD", weightPct: 100, symbols: base.rows.map(r => r.symbol) }];
}

// ── 4 · Revenue by region ────────────────────────────────────────────────────
// computeExposure already weights each company's own geographic split by the
// holding's weight. What changes here is the frame: the places are shown as
// places, and everything that is not a place · revenue the company did not
// break out, and holdings with no breakdown at all · is ONE line, "לא פורט",
// so the section adds to 100% of the portfolio.
const NOT_PLACE = new Set(["intl_unspecified", "rest_unspecified"]);

export function regions(base, segmentsBySymbol) {
  const ex = computeExposure(base.rows, segmentsBySymbol);
  const places = ex.regions.filter(r => !NOT_PLACE.has(r.id));
  const unspecifiedRevenuePct = ex.regions.filter(r => NOT_PLACE.has(r.id)).reduce((a, r) => a + r.pct, 0);
  const noBreakdownPct = ex.excluded.reduce((a, e) => a + e.weightPct, 0);
  return {
    places,                                   // [{ id, pct }] share of the whole portfolio
    notBrokenOutPct: unspecifiedRevenuePct + noBreakdownPct,
    unspecifiedRevenuePct,
    noBreakdownPct,
    excluded: ex.excluded,                    // [{ symbol, weightPct, reason }]
    perHolding: ex.perHolding || [],          // [{ symbol, year, unspecifiedShare }]
    years: [...new Set((ex.perHolding || []).map(h => h.year).filter(Boolean))].sort(),
    notes: ex.notes,
  };
}

// ── 5 · What we do not know yet ──────────────────────────────────────────────
// Per holding, the facts that are missing. A gap in information, stated as
// such · not a warning, and not a suggestion to do anything about it.
export function gaps(base, reg, portfolio = []) {
  const out = [];
  const byEx = Object.fromEntries((reg?.excluded || []).map(e => [e.symbol, e.reason]));
  const byHold = Object.fromEntries((reg?.perHolding || []).map(h => [h.symbol, h]));
  for (const r of base.rows) {
    const items = [];
    if (r.sector === SECTOR_ETF) items.push("תעודת סל · ההחזקות שבתוכה אינן במערכת, ולכן אין לה פילוח סקטורים או אזורים");
    else {
      if (!r.sector) items.push("הסקטור עוד לא סווג");
      if (reg) {
        if (byEx[r.symbol] === "no_data") items.push("דיווחי ה-SEC של החברה עוד לא נמשכו למערכת");
        else if (byEx[r.symbol] === "no_geographic_breakdown") items.push("החברה לא מפרטת הכנסות לפי אזור");
        else if (byHold[r.symbol]?.unspecifiedShare >= 0.005) {
          items.push({ unspecified: byHold[r.symbol].unspecifiedShare * 100 });
        }
      }
    }
    if (items.length) out.push({ symbol: r.symbol, weightPct: r.weightPct, items });
  }
  for (const p of base.unpriced || portfolio.filter(x => x.quote === "none")) {
    out.push({ symbol: p.symbol, weightPct: null, items: [`${noQuoteText(p)} · ההחזקה אינה בחישובי המשקל`] });
  }
  return out;
}
